import React, { useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import RichTextEditor from "../components/RichTextEditor";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Calendar, Tag, Save, X
} from "lucide-react";

export default function PostsUpdatesManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    category: "update",
    tags: "",
    published: true,
    featured_image: "",
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/posts-updates/all");
      setPosts(res.data.posts || []);
    } catch (e) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean),
      };

      if (editingPost) {
        await api.put(`/api/posts-updates/${editingPost.id}`, payload);
        toast.success("Post updated!");
      } else {
        await api.post("/api/posts-updates/create", payload);
        toast.success("Post created!");
      }

      resetForm();
      await loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save post");
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || "",
      category: post.category,
      tags: post.tags?.join(", ") || "",
      published: post.published,
      featured_image: post.featured_image || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!confirm("Delete this post?")) return;
    
    try {
      await api.delete(`/api/posts-updates/${postId}`);
      toast.success("Post deleted!");
      await loadPosts();
    } catch (e) {
      toast.error("Failed to delete post");
    }
  };

  const togglePublished = async (post) => {
    try {
      await api.put(`/api/posts-updates/${post.id}`, {
        ...post,
        published: !post.published
      });
      toast.success(post.published ? "Post unpublished" : "Post published");
      await loadPosts();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      excerpt: "",
      category: "update",
      tags: "",
      published: true,
      featured_image: "",
    });
    setEditingPost(null);
    setShowForm(false);
  };

  return (
    <AppLayout
      title="Posts & Updates Manager"
      overline="CONTENT MANAGEMENT"
      subtitle="Create and manage posts and updates for member areas"
      actions={
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary inline-flex items-center gap-2 text-xs"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Cancel" : "New Post"}
        </button>
      }
    >
      {/* Form */}
      {showForm && (
        <div
          className="rounded-lg border p-6 mb-6"
          style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
        >
          <h3 className="font-bold text-lg mb-4" style={{ color: "var(--gio-text)" }}>
            {editingPost ? "Edit Post" : "Create New Post"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="gio-input w-full"
                  placeholder="Post title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="gio-input w-full"
                >
                  <option value="update">Update</option>
                  <option value="announcement">Announcement</option>
                  <option value="news">News</option>
                  <option value="behind-the-scenes">Behind the Scenes</option>
                  <option value="exclusive">Exclusive</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                Excerpt (Short Description)
              </label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={2}
                className="gio-input w-full"
                placeholder="Brief summary..."
              />
            </div>

            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                Content *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => setFormData({ ...formData, content: value })}
                className="mb-12 h-64"
                placeholder="Write your post content with formatting..."
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="gio-input w-full"
                  placeholder="tag1, tag2, tag3"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
                  Featured Image URL
                </label>
                <input
                  type="text"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  className="gio-input w-full"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="published" className="text-sm font-bold" style={{ color: "var(--gio-text)" }}>
                Publish immediately
              </label>
            </div>

            <div className="flex gap-2">
              <button type="submit" className="btn-primary inline-flex items-center gap-2">
                <Save size={16} />
                {editingPost ? "Update Post" : "Create Post"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn-ghost"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Posts List */}
      <div
        className="rounded-lg border"
        style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
      >
        {loading ? (
          <div className="text-center py-12" style={{ color: "var(--gio-muted)" }}>
            Loading posts...
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12" style={{ color: "var(--gio-muted-3)" }}>
            <Tag size={32} className="mx-auto mb-2" />
            <p className="text-sm">No posts yet. Create your first post!</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--gio-border)" }}>
            {posts.map((post) => (
              <div key={post.id} className="p-4 hover:bg-[color:var(--gio-bg)] transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-bold text-base" style={{ color: "var(--gio-text)" }}>
                        {post.title}
                      </h4>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: "var(--gio-primary-soft)",
                          color: "var(--gio-primary)",
                        }}
                      >
                        {post.category}
                      </span>
                      {!post.published && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">
                          Draft
                        </span>
                      )}
                    </div>
                    
                    {post.excerpt && (
                      <p className="text-sm mb-2" style={{ color: "var(--gio-muted-2)" }}>
                        {post.excerpt}
                      </p>
                    )}

                    <div className="flex items-center gap-3 text-xs" style={{ color: "var(--gio-muted-3)" }}>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                      {post.tags?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Tag size={12} />
                          {post.tags.join(", ")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => togglePublished(post)}
                      className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                      title={post.published ? "Unpublish" : "Publish"}
                    >
                      {post.published ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button
                      onClick={() => handleEdit(post)}
                      className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-red-400"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
